import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MdClose } from 'react-icons/md'
import { CalendarCard } from '../../types'
import { Modal } from '../common'
import { CalendarCardModal } from './CalendarCardModal'

interface Props {
  isOpen: boolean
  onClose: () => void
  date: Date
  cards: CalendarCard[]
}

export const CardsModal: React.FC<Props> = ({
  isOpen,
  onClose,
  date,
  cards,
}) => {
  const { t } = useTranslation()
  const [selectedCardId, setSelectedCardId] = useState<number | null>(null)
  const [selectedBoardId, setSelectedBoardId] = useState<number | null>(null)

  if (!isOpen) return null

  const handleCardClick = (cardId: number, boardId: number) => {
    setSelectedCardId(cardId)
    setSelectedBoardId(boardId)
  }

  const handleCloseCardModal = () => {
    setSelectedCardId(null)
    setSelectedBoardId(null)
  }

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} title="" size="lg">
        <div>
          {/* Header */}
          <div className="border-b flex justify-between items-center">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {date.toLocaleDateString('uz-UZ', {
                  year: 'numeric',
                  month: 'numeric',
                  day: 'numeric',
                  timeZone: 'Asia/Tashkent',
                })}
              </h2>
              <p className="text-sm text-gray-500">{cards.length} ta vazifa</p>
            </div>

            <button
              className="p-2 hover:bg-gray-100 rounded-full"
              onClick={onClose}
            >
              <MdClose className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Content */}
          <div className="p-5 overflow-auto">
            {cards.length === 0 ? (
              <p className="text-center text-gray-500 py-5">
                {t('calendar.no_cards')}
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead>
                    <tr className="bg-gray-50 text-xs uppercase text-gray-600">
                      <th className="px-4 py-3 text-left">
                        {t('board.cards')}
                      </th>
                      <th className="px-4 py-3 text-left">
                        {t('calendar.board')}
                      </th>
                      <th className="px-4 py-3 text-left">
                        {t('calendar.column')}
                      </th>
                      <th className="px-4 py-3 text-left">
                        {t('calendar.due_date')}
                      </th>
                      <th className="px-4 py-3 text-left">
                        {t('calendar.members')}
                      </th>
                      <th className="px-4 py-3 text-left">
                        {t('calendar.status')}
                      </th>
                    </tr>
                  </thead>

                  <tbody className="divide-y">
                    {cards.map((c) => (
                      <tr key={c.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <button
                            onClick={() => handleCardClick(c.id, c.boardId)}
                            className="flex items-center gap-2 hover:text-blue-600 transition-colors text-left w-full"
                          >
                            <span
                              className="w-3 h-3 rounded-full flex-shrink-0"
                              style={{ backgroundColor: '#3b82f6' }}
                            />
                            <span className="truncate">{c.title}</span>
                          </button>
                        </td>

                        <td className="px-4 py-3 text-gray-600">
                          {c.boardName}
                        </td>
                        <td className="px-4 py-3 text-gray-600">
                          {c.columnName}
                        </td>

                        <td className="px-4 py-3 text-gray-600">{c.dueDate}</td>

                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-1">
                            {c.members.map((m) => (
                              <span
                                key={m.userId}
                                className="bg-blue-100 text-blue-700 px-2 py-1 rounded-full text-xs"
                              >
                                {m.name}
                              </span>
                            ))}
                          </div>
                        </td>

                        <td className="px-4 py-3">
                          {c.archived ? (
                            <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded-full text-xs">
                              {t('calendar.archived')}
                            </span>
                          ) : (
                            <span className="bg-green-100 text-green-700 px-2 py-1 rounded-full text-xs">
                              {t('calendar.active')}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </Modal>

      {/* Card Modal */}
      {selectedCardId && selectedBoardId && (
        <CalendarCardModal
          cardId={selectedCardId}
          boardId={selectedBoardId}
          onClose={handleCloseCardModal}
        />
      )}
    </>
  )
}

export default CardsModal
